import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AnalysisDocumentTypes,
  AnalysisTypes,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CheckListIcon,
  SettingsIcon,
  FolderIcon,
  Clock04Icon,
  ArrowRight02Icon,
  SearchList01Icon,
  EyeIcon,
} from '@hugeicons/core-free-icons';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { useTranslations } from 'use-intl';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainNav,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/app/ui/components/main';
import { H1, H2, H3 } from '@/app/ui/components/heading';
import AnalysisAssessment from '@/app/components/features/evaluations/analysis/NewAssesment/AnalysisAssessment';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { getEPADetailsQuery } from '@/app/hooks/queries/useEarlyPhaseAssessment';
import { SummaryFrame } from '@/app/components/features/evaluations/frames/SummaryFrame';
import { ExtractionsFrame } from '@/app/components/features/evaluations/frames/ExtractionsFrame';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Frame, FramePanel } from '@/app/ui/components/frame';
import {
  DocumentSummarizationNamespace,
  summaryTypeToDocumentType,
  summaryTypeToProxyDocumentType,
} from '@/app/components/features/evaluations/utils';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import { LinkProps } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { setupDepsOpenAtom } from '@/app/components/features/evaluations/EarlyPhaseAssessment/DependenciesSetupModal/store';
import {
  useDependenciesMutation,
  useGetDependenciesStatusQuery,
  useRefreshDependencies,
} from '@/app/components/features/evaluations/analysis/Dependencies/useDependencies';
import { useCheckAnalysisStatus } from '@/app/hooks/useCheckAnalysisStatus';
import { cn } from '@/app/utils/helpers';
import { Spinner } from '@/app/ui/components/spinner';
import { Progress, ProgressTrack, ProgressValue } from '@/app/ui/components/progress';
import { SummaryIcon } from '@/app/components/shared/EmptyIcons';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/app/ui/components/tooltip';
import { Button } from '@/app/ui/components/button';
import NewDependenciesSetupModal from '@/app/components/features/evaluations/EarlyPhaseAssessment/DependenciesSetupModal/NewDependenciesSetupModal';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/early-phase-assessments/$earlyPhaseAssessmentId/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.epaHome');
  const navigate = useNavigate();
  const { earlyPhaseAssessmentId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getEPADetailsQuery(earlyPhaseAssessmentId));

  const { data: documents } = useEntityDocuments(
    earlyPhaseAssessmentId,
    DocumentTypes.EARLY_PHASE_ASSESSMENT,
  );

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={CheckListIcon} />
                {t('title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={earlyPhaseAssessmentId}
          summaryType={SummarizationNamespaces.EARLY_PHASE_ASSESSMENT}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/files',
              params: { projectId, earlyPhaseAssessmentId },
            })
          }
        />
        <MainHeader>
          <H1>{t('titleLong')}</H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/files"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')} {!documents?.length ? null : `(${documents.length})`}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/activity"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <HugeiconsIcon icon={Clock04Icon} />
                {t('nav.activityLog')}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/settings"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <HugeiconsIcon icon={SettingsIcon} />
                {t('nav.settings')}
              </MainNavLink>
            </MainNavItem>
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t('section1Title')}</H2>
          <AnalysisAssessment
            documentType={AnalysisDocumentTypes.EARLY_PHASE_ASSESSMENT}
            id={earlyPhaseAssessmentId}
          />
          <H2 className="mb-3 mt-5">{t('section3Title')}</H2>
          <MainFrames>
            <ExtractionsFrame
              to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/extractions"
              params={{ projectId, earlyPhaseAssessmentId }}
              evaluationId={earlyPhaseAssessmentId}
              summaryType={SummarizationNamespaces.EARLY_PHASE_ASSESSMENT}
              evalLanguage={evaluation.language}
            />
          </MainFrames>
          <H2 className="mb-3 mt-5">{t('section2Title')}</H2>
          <MainFrames>
            <SummaryFrame
              to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/summary"
              params={{ projectId, earlyPhaseAssessmentId }}
              evaluationId={earlyPhaseAssessmentId}
              summaryType={SummarizationNamespaces.EARLY_PHASE_ASSESSMENT}
            />
            <DependenciesFrame
              id={earlyPhaseAssessmentId}
              to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/dependencies"
              summaryType={SummarizationNamespaces.EARLY_PHASE_ASSESSMENT}
            />
          </MainFrames>
        </MainContainer>
      </MainScrollArea>
    </>
  );
}

interface DependenciesFrameProps extends LinkProps {
  id: string;
  summaryType: DocumentSummarizationNamespace;
  className?: string;
}

function DependenciesFrame({ id, summaryType, className, ...props }: DependenciesFrameProps) {
  const documentType = summaryTypeToDocumentType[summaryType];
  const proxyDocumentType = summaryTypeToProxyDocumentType[summaryType];

  const { data: documents } = useEntityDocuments(id, documentType);

  const { areDocumentsIndexed } = useDocumentsIndexingStatus({
    documentType: proxyDocumentType,
    id,
    enabled: !!documents && documents.length > 0,
  });

  const t = useTranslations('features.dependencies');
  const tGeneral = useTranslations('general');
  const navigate = useNavigate();
  const setModalOpen = useSetAtom(setupDepsOpenAtom);
  const params = { earlyPhaseAssessmentId: id };
  const { data, isLoading } = useGetDependenciesStatusQuery(params);
  const { mutate: triggerAnalysis, isPending } = useDependenciesMutation(id);
  const { isCompleted, isProcessing } = useCheckAnalysisStatus({
    status: data?.status,
    analysisType: AnalysisTypes.DEPENDENCIES,
    refreshParams: params,
    refreshFn: useRefreshDependencies,
    isQueryLoading: isLoading,
    isMutationPending: isPending,
  });
  const progress = data?.progressPercentage ?? 0;

  const disabled = isPending || isProcessing || (!isCompleted && !areDocumentsIndexed);

  return (
    <>
      <Frame
        className={cn(
          'data-has-data:cursor-pointer data-has-data:hover:shadow-md/4 transition-shadow',
          className,
        )}
        data-has-data={isCompleted && !disabled ? '' : undefined}
        {...props}
        onClick={() => {
          if (disabled) return;
          if (isCompleted) return navigate(props);
        }}
      >
        <FramePanel className="h-38 grid place-items-center">
          {isLoading ? (
            <Spinner kind="inset" className="translate-y-0" />
          ) : isProcessing && !isPending && !isLoading ? (
            <Progress value={+progress.toFixed(0)} max={100} className={'mx-auto'}>
              <p className="text-muted text-sm mb-2 text-center">
                <Spinner size="xs" className="inline-block mr-2 mb-0.75" />
                {t('analysing')} - <ProgressValue />
              </p>
              <ProgressTrack className={'max-w-50 mx-auto'} />
            </Progress>
          ) : isCompleted ? (
            <p>
              {t.rich('found', {
                count: data?.count ?? 0,
                strong: (chunks) => (
                  <strong className="block font-medium text-4xl text-center">{chunks}</strong>
                ),
              })}
            </p>
          ) : (
            <SummaryIcon className="size-25" />
          )}
        </FramePanel>
        <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
          <div>
            <H3>{t('title')}</H3>
            <p className="text-muted mt-0.5 text-sm">{t('description')}</p>
          </div>
          {isCompleted ? (
            <HugeiconsIcon
              icon={ArrowRight02Icon}
              className="invisible group-hover:visible ml-auto max-md:hidden"
            />
          ) : null}
          <div
            data-has-data={isCompleted ? '' : undefined}
            data-processing={isProcessing ? '' : undefined}
            className="data-has-data:md:hidden data-processing:md:hidden md:absolute md:grid rounded-lg md:opacity-0 group-hover:opacity-100 md:place-items-center md:inset-0 md:bg-black/7"
          >
            <Tooltip>
              <TooltipTrigger
                delay={0}
                render={
                  <Button
                    className="max-md:w-full max-md:mt-2 disabled:md:opacity-100"
                    size="sm"
                    kind="with-icon"
                    variant={isCompleted ? 'tertiary-2' : 'secondary'}
                    pending={isPending}
                    disabled={disabled}
                    onClick={() => {
                      if (isCompleted) return navigate(props);
                      setModalOpen(true);
                    }}
                  >
                    <HugeiconsIcon icon={isCompleted ? EyeIcon : SearchList01Icon} />
                    {isCompleted ? t('buttons.view') : t('buttons.generate')}
                  </Button>
                }
              />
              {!areDocumentsIndexed && !isCompleted && (
                <TooltipPopup>{tGeneral('waitForDocumentsIndexing')}</TooltipPopup>
              )}
            </Tooltip>
          </div>
        </div>
      </Frame>
      <NewDependenciesSetupModal disableAnalysis={!areDocumentsIndexed} />
    </>
  );
}
